import { Feature, FeatureCollection, Point } from "geojson"; 
import { LngLatBounds } from 'mapbox-gl';
import * as turf from '@turf/helpers';
import convex from '@turf/convex';
import bbox from '@turf/bbox';
import center from '@turf/center';
import buffer from '@turf/buffer';

import { StationFactory } from "../../resource";
import { Query } from '../../services';
import { IAuth } from "../../AuthContext";

interface ILatLng {
  lat: number;
  lng: number;
}

interface ISystem {
  id: number;
  name: string;
  coords: ILatLng[];
  /** System center coordinate */
  lat?: number;
  lng?: number;
  center?: Feature<Point>;
  /** Station point features */
  stations?: FeatureCollection;
  /** Convex hull polygon */
  hull?: Feature;
}

class SystemCollection {
  public static getSystems = async (auth: IAuth, systemIDs: number[]): Promise<ISystem[]> => {
    // Build an IN query.
    const query = new Query("name");
    query.setFilter("system", "in", systemIDs.join(','));

    return StationFactory.getSome(auth, 0, 99999, query)
    .then(res => {
      // From the list of stations read:
      let systems: ISystem[] = [];

      res.items.forEach(station => {
        // Group the stations by system.
        let system = systems.find(s => s.id === station.system.id);
        if(!system) {
          system = { id: station.system.id, name: station.system.name, coords: [], stations: turf.featureCollection([]) };
          systems.push(system);
        }
        if(station.latitude != null && station.longitude != null) {
          system.coords.push({ lat: station.latitude, lng: station.longitude });
          system.stations.features.push(turf.point([station.longitude, station.latitude]));
        }
      });
      systems.forEach(system => {
        const pointCollection = turf.featureCollection(system.coords.map(latlng => turf.point([ latlng.lng, latlng.lat ])));
        // Calculate system hull:
        system.hull = buffer(convex(pointCollection), 10);
        system.hull.properties.id = system.id;
        system.hull.properties.name = system.name;
        // Calculate system center coordinate:
        const cent = center(pointCollection);
        cent.properties["count"] = system.stations.features.length;
        system.center = cent;
        system.lng = cent.geometry.coordinates[0];
        system.lat = cent.geometry.coordinates[1];
      });
      return systems;
    });
  }

  /**
   * Return bounds of all specified systems, as a single bounding box.
   */
  public static getBounds = (systems: ISystem[]): LngLatBounds => {
    // Calculate bounding box
    const box = bbox({type: 'FeatureCollection', features: systems.map(s => s.hull)});
    return new LngLatBounds([box[0], box[1]], [box[2], box[3]]);
  }
}

export { SystemCollection, ISystem }
